import "./questionMarkdown.css";
import { isAccountMentionElement } from "./QuestionBoxAccountMention";
import type { QuestionBoxVideoMentionData } from "./QuestionBoxVideoMention";
import { isVideoMentionElement } from "./QuestionBoxVideoMention";
import type {
	Block,
	BlockKind,
	DocMentionData,
	QuestionDocument,
} from "./questionDocument";
import { selectionBounds } from "./questionDocument";
import {
	type MdInlineKind,
	type MdToken,
	tokenizeInline,
} from "./questionMarkdownTokenizer";

/**
 * The view half of the QuestionBox editor: renders the document model
 * (questionDocument) into the contenteditable DOM and maps DOM selections back
 * to model offsets. The model is authoritative; the DOM is rebuilt from it.
 *
 * Inline constructs (`**bold**`, `` `code` ``, …) are decorated in place: their
 * marker characters stay in the DOM inside hidden `.md-mark` spans and reveal
 * only while the caret is on them. Incomplete constructs never match the
 * tokenizer, so `**foo` stays literal until the closing `**` arrives. Block
 * markers (headings, quotes, list bullets/numbers) are structural metadata on
 * the model and render as pure CSS decoration — they are never DOM text.
 */

/** Matches an atom's flanking zero-width caret anchors (mirrors useQuestionBox). */
const ZERO_WIDTH_RE = /[\u200B\u200C\uFEFF]/g;
/** Zero-width space re-emitted on both sides of an atom so Safari can render the caret there. */
const CARET_ANCHOR = "\u200B";

const stripZeroWidth = (value: string): string =>
	value.replace(ZERO_WIDTH_RE, "");
const countChars = (value: string): number => stripZeroWidth(value).length;
const isZeroWidthChar = (ch: string): boolean =>
	ch === "\u200B" || ch === "\u200C" || ch === "\uFEFF";

// --- DOM rebuild ----------------------------------------------------------

const WRAPPER_CLASS: Record<MdInlineKind, string> = {
	strong: "md-strong",
	em: "md-em",
	code: "md-code",
	strike: "md-strike",
};

const makeSpan = (className: string): HTMLSpanElement => {
	const span = document.createElement("span");
	span.className = className;
	return span;
};

/** A hidden marker span (`**`, `` ` ``, `\`, …); revealed only under the caret. */
const makeMark = (text: string): HTMLSpanElement => {
	const span = makeSpan("md-mark");
	span.textContent = text;
	return span;
};

function appendTokens(parent: Node, tokens: MdToken[]): void {
	for (const token of tokens) {
		if (token.kind === "text") {
			parent.appendChild(document.createTextNode(token.value));
		} else if (token.kind === "esc") {
			const wrapper = makeSpan("md md-esc");
			wrapper.appendChild(makeMark(token.mark));
			wrapper.appendChild(document.createTextNode(token.value));
			parent.appendChild(wrapper);
		} else {
			const wrapper = makeSpan(`md ${WRAPPER_CLASS[token.kind]}`);
			wrapper.appendChild(makeMark(token.open));
			appendTokens(wrapper, token.children);
			wrapper.appendChild(makeMark(token.close));
			parent.appendChild(wrapper);
		}
	}
}

// Headings, blockquotes and lists are structural: their kind lives on the
// document model (see questionDocument), never as marker text in the DOM. Each
// block renders a single `.md` wrapper whose marker (bullet, number, heading
// styling) is pure CSS decoration — see `appendModelBlock` below.

/**
 * Elements the caret treats as a single unit (pills + account mentions).
 * Deliberately not a type predicate: a `node is HTMLElement` guard would narrow
 * the negative branch to `never` when we're already inside an HTMLElement check.
 */
export const isAtomicElement = (node: Node): boolean =>
	isVideoMentionElement(node) || isAccountMentionElement(node);

/** Selects every atom in the input (both kinds carry their data attribute). */
export const ATOM_SELECTOR = "[data-video-mention-id], [data-mention-handle]";

/**
 * A trailing "filler" `<br>`. A single `<br>` after text is treated by the
 * browser as the line terminator and does not render a visible empty line, so a
 * trailing newline (`abc\n`) needs a second, non-semantic `<br>` for the empty
 * last line — and the caret — to show. This filler is excluded from the source
 * (line splitting, caret metric, `domToSourceText`/`buildAugmentedPrompt`) so it
 * never turns into an extra newline. Kept in sync with `data-md-filler` reads in
 * useQuestionBox.
 */
const isFillerBr = (node: Node): boolean =>
	node instanceof HTMLElement &&
	node.tagName === "BR" &&
	node.dataset.mdFiller === "true";

const makeFillerBr = (): HTMLBRElement => {
	const br = document.createElement("br");
	br.dataset.mdFiller = "true";
	return br;
};

// --- Caret preservation ---------------------------------------------------
//
// The caret is captured/restored as a "source offset": the number of source
// characters before it, where a text char counts 1 (zero-width anchors count
// 0), a <br> counts 1, and each atomic element (pill/mention) counts 1. The
// rebuild preserves every source char and reuses the same atomic nodes, so the
// metric is stable across the rebuild.

interface CaretPoint {
	container: Node;
	offset: number;
}

interface CaretRange {
	start: number;
	end: number;
	collapsed: boolean;
}

function measureNode(node: Node): number {
	if (node.nodeType === Node.TEXT_NODE) {
		return countChars(node.textContent ?? "");
	}
	if (node instanceof HTMLElement) {
		if (node.tagName === "BR") return isFillerBr(node) ? 0 : 1;
		if (isAtomicElement(node)) return 1;
		let total = 0;
		node.childNodes.forEach((child) => {
			total += measureNode(child);
		});
		return total;
	}
	return 0;
}

/** Source-offset of the point `(container, offset)` within `root`. */
function offsetOf(root: Node, container: Node, offset: number): number {
	let result = 0;
	let done = false;

	const walk = (node: Node): void => {
		if (done) return;
		if (node === container) {
			if (node.nodeType === Node.TEXT_NODE) {
				result += countChars((node.textContent ?? "").slice(0, offset));
			} else {
				for (let i = 0; i < offset && i < node.childNodes.length; i++) {
					result += measureNode(node.childNodes[i]);
				}
			}
			done = true;
			return;
		}
		if (node.nodeType === Node.TEXT_NODE) {
			result += countChars(node.textContent ?? "");
			return;
		}
		if (node instanceof HTMLElement) {
			if (node.tagName === "BR") {
				result += isFillerBr(node) ? 0 : 1;
				return;
			}
			if (isAtomicElement(node)) {
				result += 1;
				// A DOM point *inside* an atom is ambiguous, since the atom is a
				// single unit: snap it to the atom's far edge. Without this the walk
				// would never reach `container` and would measure the whole
				// document, silently sending the caret to the end.
				if (node.contains(container)) done = true;
				return;
			}
			const kids = node.childNodes;
			for (let i = 0; i < kids.length && !done; i++) {
				walk(kids[i]);
			}
		}
	};

	walk(root);
	return result;
}

/** String index in `s` at source-metric position `metric` (skips zero-width). */
function realOffsetForMetric(s: string, metric: number): number {
	let count = 0;
	for (let i = 0; i < s.length; i++) {
		if (count === metric) return i;
		if (!isZeroWidthChar(s[i])) count++;
	}
	return s.length;
}

/** Resolves a source-offset back to a `(container, offset)` DOM point. */
function locate(root: HTMLElement, target: number): CaretPoint {
	let remaining = target;
	let placed: CaretPoint | null = null;
	let fallback: CaretPoint | null = null;

	const walk = (node: Node): void => {
		const kids = node.childNodes;
		for (let i = 0; i < kids.length; i++) {
			if (placed) return;
			const child = kids[i];
			if (child.nodeType === Node.TEXT_NODE) {
				const s = child.textContent ?? "";
				const m = countChars(s);
				if (remaining <= m) {
					placed = {
						container: child,
						offset: realOffsetForMetric(s, remaining),
					};
					return;
				}
				remaining -= m;
			} else if (
				child instanceof HTMLElement &&
				(child.tagName === "BR" || isAtomicElement(child))
			) {
				// Filler <br>s are zero-length: not a caret stop, no source offset.
				if (isFillerBr(child)) continue;
				if (remaining === 0) {
					placed = { container: node, offset: i };
					return;
				}
				remaining -= 1;
				if (remaining === 0) fallback = { container: node, offset: i + 1 };
			} else if (child instanceof HTMLElement) {
				walk(child);
				if (placed) return;
			}
		}
	};

	walk(root);
	return (
		placed ?? fallback ?? { container: root, offset: root.childNodes.length }
	);
}

/**
 * Keeps a collapsed caret out of a hidden marker span: if it lands at the outer
 * edge of an opening/closing marker, hop just outside the `.md` wrapper so the
 * markers stay hidden (rather than revealing a completed token the moment the
 * caret sits right after it).
 */
function nudgeOutOfHiddenMark(
	root: HTMLElement,
	point: CaretPoint,
): CaretPoint {
	const { container, offset } = point;
	if (container.nodeType !== Node.TEXT_NODE) return point;
	const parent = container.parentElement;
	const mark = parent?.closest(".md-mark");
	if (!mark || !root.contains(mark)) return point;
	const wrapper = mark.closest(".md");
	if (!wrapper?.parentNode) return point;
	// Block-line markers (list bullet/number, heading, blockquote) anchor the
	// caret *inside* the line, so never hop it outside the wrapper — otherwise a
	// caret placed after an empty new bullet's marker would escape the list.
	if (
		wrapper.classList.contains("md-list") ||
		wrapper.classList.contains("md-heading") ||
		wrapper.classList.contains("md-quote")
	) {
		return point;
	}
	const wrapperParent = wrapper.parentNode;
	const wrapperIndex = Array.prototype.indexOf.call(
		wrapperParent.childNodes,
		wrapper,
	);
	const len = (container.textContent ?? "").length;
	if (offset >= len && wrapper.lastChild === mark) {
		return { container: wrapperParent, offset: wrapperIndex + 1 };
	}
	if (offset <= 0 && wrapper.firstChild === mark) {
		return { container: wrapperParent, offset: wrapperIndex };
	}
	return point;
}

const clampOffset = (point: CaretPoint): number => {
	const max =
		point.container.nodeType === Node.TEXT_NODE
			? (point.container.textContent ?? "").length
			: point.container.childNodes.length;
	return Math.max(0, Math.min(point.offset, max));
};

function restoreCaret(
	root: HTMLElement,
	selection: Selection | null,
	caret: CaretRange,
): void {
	if (!selection) return;
	try {
		let start = locate(root, caret.start);
		let end = caret.collapsed ? start : locate(root, caret.end);
		if (caret.collapsed) {
			start = nudgeOutOfHiddenMark(root, start);
			end = start;
		}
		const range = document.createRange();
		range.setStart(start.container, clampOffset(start));
		range.setEnd(end.container, clampOffset(end));
		selection.removeAllRanges();
		selection.addRange(range);
	} catch {
		const range = document.createRange();
		range.selectNodeContents(root);
		range.collapse(false);
		selection.removeAllRanges();
		selection.addRange(range);
	}
}

/**
 * An ordered piece of a line: a run of literal text, or an atomic element
 * (video-mention pill or account-mention chip) that the tokenizer must not
 * touch and that is reused (moved) into the rebuilt DOM.
 */
type LineAtom =
	| { kind: "text"; value: string }
	| { kind: "pill"; node: HTMLElement }
	| { kind: "mention"; node: HTMLElement };

/**
 * Renders one atom into `parent`. Both atom kinds are flanked by zero-width
 * caret anchors: without a real text node beside them the caret has nowhere to
 * land, so it can't be placed on the outer side of an atom that starts or ends a
 * line, and a click can strand the selection inside the atom itself.
 */
function appendAtom(parent: Node, atom: LineAtom): void {
	if (atom.kind === "text") {
		appendTokens(parent, tokenizeInline(atom.value));
		return;
	}
	parent.appendChild(document.createTextNode(CARET_ANCHOR));
	parent.appendChild(atom.node);
	parent.appendChild(document.createTextNode(CARET_ANCHOR));
}

// --- Reveal ---------------------------------------------------------------

const ACTIVE_CLASS = "md--active";

/**
 * Reveals the marker characters of the `.md` token(s) the selection touches by
 * toggling {@link ACTIVE_CLASS}. Called on `selectionchange`. All ancestor
 * wrappers of each endpoint are revealed (so nested tokens show together), plus
 * any wrapper intersected by a non-collapsed selection.
 */
export function updateActiveMarkdownToken(input: HTMLElement): void {
	for (const el of input.querySelectorAll(`.${ACTIVE_CLASS}`)) {
		el.classList.remove(ACTIVE_CLASS);
	}

	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) return;
	const range = selection.getRangeAt(0);
	if (!input.contains(range.commonAncestorContainer)) return;

	const activateAncestors = (node: Node | null) => {
		let el =
			node?.nodeType === Node.TEXT_NODE
				? node.parentElement
				: (node as Element | null);
		while (el && el !== input) {
			if (el.classList.contains("md")) el.classList.add(ACTIVE_CLASS);
			el = el.parentElement;
		}
	};

	activateAncestors(range.startContainer);
	activateAncestors(range.endContainer);

	if (!selection.isCollapsed) {
		for (const el of input.querySelectorAll(".md")) {
			if (selection.containsNode(el, true)) el.classList.add(ACTIVE_CLASS);
		}
	}
}

// --- Model-driven rendering -----------------------------------------------
//
// The controlled editor renders the view straight from the document model
// (questionDocument), rather than reading the DOM back. Atom nodes (pills,
// mention chips) are created from their model data via caller-supplied
// factories, so their event listeners are freshly bound on every render.

/** Creates the atom DOM nodes for a rendered document (listeners freshly bound). */
export interface DocumentRenderFactories {
	createPill: (data: QuestionBoxVideoMentionData) => HTMLElement;
	createMention: (data: DocMentionData) => HTMLElement;
}

/** The `.md*` wrapper classes for a structural block kind, or null for a paragraph. */
function blockClass(kind: BlockKind): string | null {
	switch (kind.type) {
		case "heading":
			return `md md-heading md-h${kind.level}`;
		case "quote":
			return "md md-quote";
		case "list-ul":
			return "md md-list md-list-ul";
		case "list-ol":
			return "md md-list md-list-ol";
		default:
			return null;
	}
}

/** Appends a block's content inlines (text tokenised, atoms rebuilt) into `parent`. */
function appendBlockContent(
	parent: Node,
	block: Block,
	factories: DocumentRenderFactories,
): void {
	for (const inline of block.inlines) {
		if (inline.kind === "text") {
			appendAtom(parent, { kind: "text", value: inline.value });
		} else if (inline.kind === "pill") {
			appendAtom(parent, {
				kind: "pill",
				node: factories.createPill(inline.data),
			});
		} else {
			appendAtom(parent, {
				kind: "mention",
				node: factories.createMention(inline.data),
			});
		}
	}
}

/**
 * Renders one block into `fragment`. Paragraphs render their content bare; other
 * kinds render a `.md*` wrapper whose marker is pure decoration (a bullet, a
 * number via `data-md-*`, heading/quote styling) — the marker text is never in
 * the DOM. Empty wrappers get a zero-width anchor so the caret can land inside.
 */
function appendModelBlock(
	fragment: DocumentFragment,
	block: Block,
	factories: DocumentRenderFactories,
): void {
	const className = blockClass(block.kind);
	if (!className) {
		appendBlockContent(fragment, block, factories);
		return;
	}
	const wrapper = makeSpan(className);
	if (block.kind.type === "list-ol") {
		wrapper.dataset.mdNumber = String(block.kind.number);
		wrapper.dataset.mdDelim = block.kind.delim;
	}
	appendBlockContent(wrapper, block, factories);
	if (wrapper.childNodes.length === 0) {
		wrapper.appendChild(document.createTextNode(CARET_ANCHOR));
	}
	fragment.appendChild(wrapper);
}

/**
 * Renders a {@link QuestionDocument} into `input`, replacing its contents, and
 * places the caret/selection from the model's own offsets. This is the view
 * half of the document/view split: the model is authoritative, so the DOM is
 * rebuilt from it (never the other way round for structural edits).
 */
export function renderDocument(
	input: HTMLElement,
	doc: QuestionDocument,
	factories: DocumentRenderFactories,
): void {
	const fragment = document.createDocumentFragment();
	doc.blocks.forEach((block, index) => {
		if (index > 0) fragment.appendChild(document.createElement("br"));
		appendModelBlock(fragment, block, factories);
	});

	const last = doc.blocks[doc.blocks.length - 1];
	if (
		doc.blocks.length > 1 &&
		last.kind.type === "paragraph" &&
		last.inlines.length === 0
	) {
		fragment.appendChild(makeFillerBr());
	}

	input.replaceChildren(fragment);

	const selection = window.getSelection();
	if (!selection) return;
	const [start, end] = selectionBounds(doc.selection);
	restoreCaret(input, selection, { start, end, collapsed: start === end });
}

/** Source-offset of a DOM point within `input` (maps a DOM selection to the model). */
export function domOffsetOf(
	input: HTMLElement,
	container: Node,
	offset: number,
): number {
	return offsetOf(input, container, offset);
}

/**
 * The DOM point for a source-offset — the inverse of {@link domOffsetOf}, and
 * the same resolution {@link renderDocument} uses to place the caret, so a
 * caret moved from the model lands where a re-render would have put it.
 */
export function domPointAt(
	input: HTMLElement,
	offset: number,
): { container: Node; offset: number } {
	const point = locate(input, offset);
	return { container: point.container, offset: clampOffset(point) };
}
